import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

// Render-time exception catch-all. Without this, an uncaught throw blanks
// the whole tree to a white screen with no recovery path. We intentionally
// don't display the error's message — it could include path fragments,
// internal field names, or be crafted in a way that confuses the user
// about Rialto vs. Google state. The user can refresh or come back later.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Goes to the browser console; the server can't see this. The signal
    // here is "did the SPA crash at all", not the specific cause.
    // eslint-disable-next-line no-console
    console.error('Rialto crashed:', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="grid min-h-dvh place-items-center bg-[var(--bg-sunken)] p-4">
        <div className="w-[420px] max-w-full rounded-xl border border-[var(--border)] bg-[var(--bg)] p-8 shadow-md">
          <h1 className="mb-1 text-xl font-semibold">Something went wrong</h1>
          <p className="mb-6 text-[var(--text-muted)]">
            Rialto hit an unexpected error. Reload to try again — your data is
            safe on Google Calendar.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full rounded-lg bg-[var(--accent)] py-3 text-center font-medium text-[var(--accent-fg)]"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
